import { useState, useEffect } from 'react';
import { Box, Flex, Input, Button, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import * as authApi from '../../api/auth';

interface EmailVerificationFlowProps {
  email: string;
  onSuccess?: () => void;
  onBack?: () => void;
}

const EmailVerificationFlow = ({ email, onSuccess, onBack }: EmailVerificationFlowProps) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError(t('verification.codeMustBe6Digits'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await authApi.verifyEmail({ email, code });
      toast.success(t('verification.emailVerified'));
      onSuccess?.();
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || t('verification.verificationFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError(null);

    try {
      await authApi.resendVerificationCode(email);
      toast.success(t('verification.codeResent'));
      setResendCooldown(60);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || t('verification.resendFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Flex direction="column" rowGap="24px">
      <Flex direction="column" rowGap="8px">
        <Box fontSize="24px" fontWeight="500" color="neutral.100">
          {t('verification.verifyYourEmail')}
        </Box>
        <Box fontSize="14px" fontWeight="400" color="neutral.400">
          {t('verification.codeSentTo', { email })}
        </Box>
      </Flex>

      <Flex direction="column" rowGap="8px">
        <Flex justifyContent="space-between">
          <Box fontWeight="400" fontSize="14px" lineHeight="150%" color="neutral.300">
            {t('verification.verificationCode')}
          </Box>
          <Box textAlign="center" color="error.600" fontSize="14px" fontWeight="300">
            {error}
          </Box>
        </Flex>
        <Input
          type="text"
          inputMode="numeric"
          maxLength={6}
          p="10px"
          fontWeight="400"
          fontSize="14px"
          lineHeight="150%"
          placeholder={t('verification.enterCode')}
          color="neutral.300"
          borderWidth="1px"
          borderColor="neutral.700"
          backgroundColor="neutral.800"
          borderRadius="8px"
          value={code}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '');
            setCode(value);
            setError(null);
          }}
        />
      </Flex>

      <Flex alignItems="center">
        <Button
          height="fit-content"
          width="100%"
          py="8px"
          fontWeight="500"
          fontSize="14px"
          color="neutral.900"
          backgroundColor="neutral.100"
          loading={isLoading}
          disabled={isLoading || code.length !== 6}
          onClick={handleVerify}
        >
          {t('auth.verifyEmail')}
        </Button>
      </Flex>

      <Flex justifyContent="center" alignItems="center" columnGap="4px">
        <Box fontSize="14px" fontWeight="400" color="neutral.400">
          {t('verification.didntReceiveCode')}
        </Box>
        <Text
          as="button"
          fontSize="14px"
          fontWeight="500"
          color={resendCooldown > 0 ? 'neutral.500' : 'neutral.200'}
          textDecoration="underline"
          cursor={resendCooldown > 0 ? 'not-allowed' : 'pointer'}
          bg="none"
          border="none"
          onClick={resendCooldown > 0 || isResending ? undefined : handleResendCode}
          _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
          _hover={resendCooldown > 0 ? {} : { color: 'neutral.100' }}
        >
          {resendCooldown > 0 ? t('verification.resendIn', { seconds: resendCooldown }) : t('verification.resend')}
        </Text>
      </Flex>

      {onBack && (
        <Flex justifyContent="center">
          <Text
            as="button"
            fontSize="14px"
            fontWeight="400"
            color="neutral.400"
            textDecoration="underline"
            cursor="pointer"
            bg="none"
            border="none"
            onClick={onBack}
            _hover={{ color: 'neutral.300' }}
          >
            {t('verification.backToSignup')}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export default EmailVerificationFlow;
